import {
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import type { DocumentNode } from 'graphql'

import Flex from 'shared/ui/Flex'
import Text from 'shared/ui/Text'
import Button from 'shared/ui/Button_2'
import { GET_PROJECT_EXPORTS } from 'data/layoutcreator/queries/export'
import { GET_PRODUCTION_EXPORTS } from 'data/layoutcreator/queries/production'
import { START_COVER_PDF_EXPORT } from 'data/layoutcreator/mutations/projects'
import {
  CHECK_FOR_COVER_FILE,
  GET_PROJECT_TOKEN
} from 'data/layoutcreator/queries/projects'
import AdminLayout from 'shared/layout/Admin'
import { getRestUrl } from 'shared/helpers/url'
import useInterval from 'shared/hooks/useInterval'
import {
  ProductionExportStatuses,
  ProductionExportType
} from 'shared/types/layoutcreator/graphql'

import ExportTable from './components/ExportTable'
import ProductionExportTable from './components/ProductionExportTable'
import Header from './components/Header'
import { ProjectExportStatuses, ProjectExportTypes } from './types'
import { getDownloadPdfUrl } from './utils.'
import ProjectExportModal from './components/ProjectExportModal'
import CoverExportModal from './components/CoverExportModal'

const getCoverExportUrl = ({
  projectId,
  groupId,
  coverFileId,
  token
}: {
  projectId: string
  groupId: string
  coverFileId: string
  token: string
}) =>
  getRestUrl(
    `cover/export?projectId=${encodeURIComponent(
      projectId
    )}&groupId=${encodeURIComponent(
      groupId
    )}&coverFileId=${encodeURIComponent(
      coverFileId
    )}&token=${encodeURIComponent(token)}`
  )

const downloadCoverPdf = ({
  projectId,
  groupId,
  coverFileId,
  token
}: {
  projectId: string
  groupId: string
  coverFileId: string
  token: string
}) => {
  const link = document.createElement('a')
  link.href = getCoverExportUrl({
    projectId,
    groupId,
    coverFileId,
    token
  })
  link.download = 'cover.pdf'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const AdminProjectExport: React.FC = () => {
  const { formatMessage } = useIntl()
  const { groupId, projectId } = useParams()

  const [isModalOpened, setIsModalOpened] = useState(false)
  const [isCoverModalOpened, setIsCoverModalOpened] = useState(false)
  const [currentExportId, setCurrentExportId] = useState<
    string | null
  >(null)
  const [coverFileId, setCoverFileId] = useState<string>('')
  const [isCoverExporting, setIsCoverExporting] =
    useState<boolean>(false)

  const { data: exportItemsData } = useQuery(GET_PROJECT_EXPORTS, {
    variables: {
      groupId: groupId!,
      projectId: projectId!
    }
  })
  const { data: projectTokenData } = useQuery(GET_PROJECT_TOKEN, {
    variables: {
      groupId: groupId!,
      projectId: projectId!
    },
    skip: !groupId || !projectId
  })
  const projectToken = projectTokenData?.getProjectToken

  const exportItems = useMemo(() => {
    if (!exportItemsData) {
      return []
    }

    const result = [...exportItemsData.getProjectExports]

    return result.reverse()
  }, [exportItemsData])

  // `getProductionExports` returns both preview exports (sourced from
  // `exportModel`) and production exports (sourced from
  // `productionExportModel`). Preview entries are already visible in the
  // regular export table above, so we filter them out here to avoid
  // showing the same record twice. What's left is production exports —
  // exactly the records that used to be invisible from the admin panel
  // and prevented stuck users from starting a new export.
  const { data: productionExportsData } = useQuery(
    GET_PRODUCTION_EXPORTS,
    {
      variables: {
        groupId: groupId!,
        projectId: projectId!
      },
      skip: !groupId || !projectId,
      fetchPolicy: 'cache-and-network'
    }
  )

  const productionExportItems = useMemo(() => {
    const items =
      productionExportsData?.getProductionExports?.filter(
        item => item.type !== ProductionExportType.Preview
      ) ?? []

    return [...items]
      .sort(
        (a, b) =>
          new Date(b.startedAt).getTime() -
          new Date(a.startedAt).getTime()
      )
      .map(item => ({
        id: item.id,
        type: item.type as ProductionExportType,
        pages: item.pages,
        startedAt: item.startedAt,
        authorFullName: item.author?.fullName || '-',
        status: item.status as ProductionExportStatuses
      }))
  }, [productionExportsData])

  const [
    startCoverPdfExport,
    { loading: isStartCoverExportLoading }
  ] = useMutation(START_COVER_PDF_EXPORT as DocumentNode)

  const [checkForCoverFile] = useLazyQuery(
    CHECK_FOR_COVER_FILE as DocumentNode,
    {
      fetchPolicy: 'network-only'
    }
  )

  useInterval(async () => {
    if (
      !coverFileId ||
      !isCoverExporting ||
      !groupId ||
      !projectId ||
      !projectToken
    ) {
      return
    }

    const { data } = await checkForCoverFile({
      variables: {
        projectId,
        groupId,
        coverFileId
      }
    })

    if (!data?.checkForCoverFile) {
      return
    }

    setIsCoverExporting(false)
    setCoverFileId('')
    downloadCoverPdf({
      projectId,
      groupId,
      coverFileId,
      token: projectToken
    })
    toast.success(formatMessage({ id: 'Project.export.cover.ready' }))
  }, 3000)

  const handleStartCoverExport = async (outputProfile: ProjectExportTypes) => {
    if (
      !groupId ||
      !projectId ||
      isCoverExporting ||
      isStartCoverExportLoading
    ) {
      return
    }

    setIsCoverModalOpened(false)

    const result = await toast.promise(
      startCoverPdfExport({
        variables: {
          projectId,
          outputProfile
        }
      }),
      {
        loading: formatMessage({
          id: 'Project.export.cover.start.loading'
        }),
        success: formatMessage({
          id: 'Project.export.cover.start.success'
        }),
        error: formatMessage({
          id: 'Project.export.cover.start.error'
        })
      }
    )

    const newCoverFileId = result?.data?.startCoverPdfExport
    if (typeof newCoverFileId !== 'string' || !newCoverFileId) {
      toast.error(
        formatMessage({ id: 'Project.export.cover.start.error' })
      )
      return
    }

    setCoverFileId(newCoverFileId)
    setIsCoverExporting(true)
  }

  return (
    <AdminLayout
      renderHeaderContent={() => <Header />}
      contentType="island"
    >
      <Flex justifyContent='space-between' marginBottom={48}>
        <Flex direction='column' rowGap={12}>
          <Text
            tag='h1'
            id='AdminExport.title'
            size={28}
            weight={600}
          />

          <Text
            tag='span'
            id='AdminExport.description'
            size={14}
          />
        </Flex>

        <Flex columnGap={16} alignItems='center'>
          <Button
            textId='AdminExport.buttons.exportCover'
            intent='secondary-base'
            size='sm'
            minHeight={36}
            onPress={() => setIsCoverModalOpened(true)}
            isDisabled={isCoverExporting || isStartCoverExportLoading}
            loading={isCoverExporting || isStartCoverExportLoading}
          />
          <Button
            textId='AdminExport.buttons.start'
            intent='primary-brand-02'
            size='lg'
            minHeight={44}
            onPress={() => setIsModalOpened(true)}
          />
        </Flex>
      </Flex>

      <ExportTable
        items={exportItems.map(item => ({
          id: item.id,
          type: item.outputProfile as ProjectExportTypes,
          pagesCount: item.pages.length,
          authorFullName: item.author?.fullName || '-',
          date: item.startedAt,
          status: item.status as ProjectExportStatuses,
          downloadUrl:
            (item.status === ProjectExportStatuses.completed ||
              item.status === ProjectExportStatuses.warning) &&
            projectToken
              ? getDownloadPdfUrl({
                  groupId: groupId!,
                  projectId: projectId!,
                  exportId: item.id,
                  token: projectToken
                })
              : null,
          error: item.error || null
        }))}
        onExportClick={exportId => {
          setCurrentExportId(exportId)
          setIsModalOpened(true)
        }}
      />

      <Flex direction='column' rowGap={12} marginTop={48}>
        <Text
          tag='h2'
          text='Production exports'
          size={20}
          weight={600}
        />
        <Text
          tag='span'
          text='Final / proof copies sent to CC. Delete a stuck entry to unblock the user from starting a new attempt.'
          size={13}
        />
        <ProductionExportTable
          groupId={groupId!}
          projectId={projectId!}
          items={productionExportItems}
        />
      </Flex>

      <ProjectExportModal
        isOpen={isModalOpened}
        closeModal={() => setIsModalOpened(false)}
        exportId={currentExportId}
        groupId={groupId!}
        projectId={projectId!}
      />

      <CoverExportModal
        isOpen={isCoverModalOpened}
        closeModal={() => setIsCoverModalOpened(false)}
        onConfirm={handleStartCoverExport}
        isLoading={isCoverExporting || isStartCoverExportLoading}
      />
    </AdminLayout>
  )
}

export default AdminProjectExport
